import { useEffect, useRef, useState, useNavigate, useSearchParams } from "@modica-library/react19";
import type { FormEvent } from "react";
import { Button, Input, InputPhone } from "../ui";
// Dark variant: the standard logo's "txt" letters are white (built for the
// blue topnav) and vanish on this page's light panel.
import eziLogo from "../assets/ezilogo-dark.svg";
import { changeMobile, requestOtp, verifyOtp } from "../lib/api";
import verifyArt from "../assets/ezi-2fa.png";
import "./Verify.css";

export function Verify() {
  const [params] = useSearchParams();
  const token = params.get("t") ?? "";
  const navigate = useNavigate();

  const [mobile, setMobile] = useState("your mobile");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [resend, setResend] = useState(0);
  const [changingMobile, setChangingMobile] = useState(false);
  const [newMobile, setNewMobile] = useState("");
  const [mobileError, setMobileError] = useState<string | undefined>();
  const requested = useRef(false);

  // Step 2: request the OTP once when the page loads. Without a token there's
  // nothing to verify (expired session redirect, or a direct visit) — explain
  // instead of firing a doomed request.
  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    if (!token) {
      setError(
        params.get("expired")
          ? "Your demo session has expired. Open the link from your sign-up email to start again."
          : "This page needs the link from your sign-up email.",
      );
      return;
    }
    requestOtp(token)
      .then((r) => {
        setMobile(r.maskedMobile);
        setResend(r.resendInSeconds);
      })
      .catch(() => setError("We couldn't start verification. Please try again."));
  }, [token, params]);

  // Resend countdown.
  useEffect(() => {
    const id = setInterval(() => setResend((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, []);

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const session = await verifyOtp(token, code.trim());
      sessionStorage.setItem("demoSession", JSON.stringify(session));
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  async function onChangeMobile(e: FormEvent) {
    e.preventDefault();
    // The phone input may format with spaces/hyphens/parens — normalise first.
    const mobile = newMobile.replace(/[^0-9+]/g, "");
    if (!/^\+?\d{8,15}$/.test(mobile)) {
      setMobileError("Enter a valid mobile number.");
      return;
    }
    setBusy(true);
    setMobileError(undefined);
    try {
      const r = await changeMobile(token, mobile);
      setMobile(r.maskedMobile);
      setResend(r.resendInSeconds);
      setCode("");
      setError(undefined);
      setNewMobile("");
      setChangingMobile(false);
    } catch (err) {
      setMobileError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="verify">
      <aside className="verify__art">
        <img src={verifyArt} alt="" aria-hidden="true" />
      </aside>
      <main className="verify__panel">
        <div className="verify__content">
          <img src={eziLogo} alt="EziTxt" height={40} />
          <h1 className="verify__title">Let’s get you access</h1>
          {changingMobile ? (
            <>
              <p className="verify__lede">
                No worries – tell us where to reach you and we’ll send a new code via SMS.
              </p>
              <form onSubmit={onChangeMobile} className="verify__form">
                <div className="verify__row">
                  <InputPhone
                    label="Mobile Number"
                    placeholder="+64 21 123 4567"
                    value={newMobile}
                    error={mobileError}
                    onChange={(value) => setNewMobile(value)}
                    enableCountrySelector
                    country="NZ"
                    className="verify__mobile"
                  />
                  <Button type="submit" disabled={busy || newMobile.trim().length < 8} className="verify__verify">
                    {busy ? "Sending…" : "Send code"}
                  </Button>
                </div>
              </form>
              <div className="verify__or">or</div>
              <button
                type="button"
                className="verify__alt"
                onClick={() => {
                  setChangingMobile(false);
                  setMobileError(undefined);
                }}
              >
                Enter the code sent to {mobile}
              </button>
            </>
          ) : (
            <>
              <p className="verify__lede">
                Oh hey there – check your phone! It’s us. Enter the code we sent to <strong>{mobile}</strong> via SMS.
              </p>
              <form onSubmit={onVerify} className="verify__form">
                <div className="verify__row">
                  <Input
                    label="Access Code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="- - - - - -"
                    value={code}
                    error={error}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
                    className="verify__code"
                  />
                  <Button type="submit" disabled={busy || code.length < 4} className="verify__verify">
                    {busy ? "Verifying…" : "Verify"}
                  </Button>
                </div>
              </form>
              <div className="verify__resend">
                {resend > 0 ? (
                  <span className="verify__timer">Can resend code in {resend}s</span>
                ) : (
                  <button
                    type="button"
                    className="verify__linkbtn"
                    onClick={() => requestOtp(token).then((r) => setResend(r.resendInSeconds)).catch(() => {})}
                  >
                    Resend code
                  </button>
                )}
              </div>
              <div className="verify__or">or</div>
              <button type="button" className="verify__alt" onClick={() => setChangingMobile(true)}>
                Use a different mobile number
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
